/* cardfit.js — clamp a scrolling card list to a WHOLE number of cards.
 *
 * Problem (Xav 2026-06-14): the Top-player / lineup card lists are
 * flex-1 scroll areas, so the panel height rarely divides evenly into
 * card height — the last visible card gets sliced in half (the "card 10"
 * bug). Fix: size the list's max-height down to the largest whole number
 * of cards that fits, leaving a clean bottom edge. The rest scroll.
 *
 * Zoom-safe: the UI "Text size" setting uses CSS `zoom`, which scales
 * getBoundingClientRect() but NOT offsetHeight / clientHeight / computed
 * padding (those stay in layout px). We measure ONLY with the layout-px
 * family so the maths is zoom-independent — mixing the two families is
 * exactly what broke earlier viewport measurements.
 *
 * Teardown (Xav 2026-06-15): Alpine has no destroy hook for x-init, so the
 * per-list ResizeObserver + MutationObserver would leak when a card list
 * unmounts (section switch, dashboard flip). We register every observer
 * pair and SWEEP detached ones on each new call — same approach as LSChart
 * — so the live observer count stays bounded to what's actually mounted.
 *
 * Usage (Alpine): on the scroll container,
 *     x-init="LSCardFit($el)"  class="ls-cardlist ..."
 * The container's direct children are the cards. We observe the PARENT
 * (panel-driven, stable height) to avoid a feedback loop when we shrink
 * the list itself, plus a MutationObserver so it re-fits once data lands.
 */
window.LSCardFit = (function () {
  const active = new Set();   // { listEl, ro, mo }

  /** Disconnect + drop any observer pair whose list has left the document. */
  function sweep() {
    for (const e of [...active]) {
      if (!e.listEl || !document.contains(e.listEl)) {
        try { e.ro.disconnect(); } catch (_) {}
        try { e.mo.disconnect(); } catch (_) {}
        if (e.listEl) e.listEl.__cardFit = false;   // allow a remount to re-init
        active.delete(e);
      }
    }
  }

  return function (listEl) {
    sweep();                                  // reap observers from unmounted lists
    if (!listEl || listEl.__cardFit) return;  // idempotent for a live element
    listEl.__cardFit = true;
    const parent = listEl.parentElement;
    if (!parent) return;

    const recompute = () => {
      // The first CARD - an Alpine <template> (x-for / x-if) is an element
      // too, has no height, and as firstElementChild it silently stopped
      // every list that starts with one from fitting (Phase 6 library pass).
      const card = [...listEl.children].find(c => c.tagName !== 'TEMPLATE');
      if (!card) { listEl.style.maxHeight = ''; return; }   // no data yet

      const ls   = getComputedStyle(listEl);
      const padT = parseFloat(ls.paddingTop)    || 0;
      const padB = parseFloat(ls.paddingBottom) || 0;
      const gap  = parseFloat(ls.rowGap)        || 0;
      const cardH = card.offsetHeight;                       // layout px
      if (!cardH) return;

      const ps   = getComputedStyle(parent);
      const avail = parent.clientHeight
        - (parseFloat(ps.paddingTop)    || 0)
        - (parseFloat(ps.paddingBottom) || 0);
      if (avail <= 0) return;

      const unit = cardH + gap;
      // n cards need: padT + padB + n*cardH + (n-1)*gap  ≤ avail
      let n = Math.floor((avail - padT - padB + gap) / unit);
      if (n < 1) n = 1;
      const h = padT + padB + n * cardH + (n - 1) * gap;
      listEl.style.maxHeight = Math.round(h) + 'px';
    };

    const ro = new ResizeObserver(recompute);
    ro.observe(parent);                              // panel-driven, no loop
    const mo = new MutationObserver(recompute);
    mo.observe(listEl, { childList: true });
    active.add({ listEl, ro, mo });
    requestAnimationFrame(recompute);
  };
})();

/* LSFit — make a panel's content fit its height with nothing spilling and
 * nothing sliced (Xav 2026-10-05). Each panel picks the rule that suits what
 * it shows:
 *
 *   LSFit.rows(listEl)  BUDGET - show as many WHOLE rows as fit and hide the
 *       rest from the bottom, so order the list by importance. Rows marked
 *       [data-fit-keep] (a footer) always stay; an element marked
 *       [data-fit-more] inside the list gets "+N more" when rows are hidden.
 *       Bonus Watch: the closest chases matter, the tail can go.
 *
 *   LSFit.chips(rowEl)  the same budget across: a nowrap row of chips shows
 *       whole chips and a "+N" chip ([data-fit-more="short"]), never a
 *       chip cut mid-number. The row itself is the box. Race lanes.
 *
 *   LSFit.all(el, deep)  SHOW ALL - never drop anything; if it overflows,
 *       zoom it down until the whole of it fits. Dream Team: half a team is
 *       no team.
 *
 * The box is the element that clips (overflow auto/hidden, height set by the
 * panel): the nearest [data-fit-box] or else the parent. Overflow is read off
 * the box's scrollHeight vs clientHeight - the same layout-px family on both
 * sides, so the UI "Text size" zoom cannot skew it (see LSCardFit above). A
 * box that grows with its content (phone stack) never overflows, so neither
 * rule does anything there.
 *
 * Refits when the box resizes and when the content changes. rows() watches
 * the whole subtree, because a row can grow inside itself (a card gaining a
 * line) without the list changing; its own "+N more" writes are ignored so
 * it cannot trigger itself. all() watches only the rows' parent (tbody)
 * unless asked to go deep: x-text replaces a text node on every write, so
 * deep on a panel with tweened numbers (Dream Team) would refit every
 * frame. Panels without tweens (Game Script, D/ST) pass deep. Observers on
 * detached elements are swept on each call, as LSCardFit does, because
 * x-init has no teardown. */
window.LSFit = (function () {
  const active = new Set();   // { el, ro, mo }

  function sweep() {
    for (const e of [...active]) {
      if (!document.contains(e.el)) {
        // each entry disconnects what it has and clears the flag that
        // registered it - slide() has no ResizeObserver and sets __lsSlide,
        // and the old sweep threw on it and reset the wrong flag (audit v2
        // run 3, CODE-16)
        if (e.ro) e.ro.disconnect();
        if (e.mo) e.mo.disconnect();
        e.el[e.flag || '__lsFit'] = false;
        active.delete(e);
      }
    }
  }

  const overflows = (box) => box.scrollHeight > box.clientHeight + 1;
  const overflowsX = (box) => box.scrollWidth > box.clientWidth + 1;

  /* The "+N" / "+N more" tells you what it is hiding (Xav 2026-10-06): hover
   * or focus it and a small list of the hidden rows shows - each row's
   * data-fit-label, else its text - twelve at most, then "and N more".
   * One shared tip for the page, built on demand. */
  let tipEl = null;
  function tipLabel(n) {
    const t = n.dataset.fitLabel || n.getAttribute('aria-label') || n.textContent || '';
    const s = t.replace(/\s+/g, ' ').trim();
    return s.length > 64 ? s.slice(0, 63) + '…' : s;
  }
  function showTip(more) {
    const list = more.__fitList;
    if (!list) return;
    const hidden = [...list.children].filter(n => n.tagName !== 'TEMPLATE' && !n.hasAttribute('data-fit-keep') && n.style.display === 'none');
    if (!hidden.length) return;
    if (!tipEl) { tipEl = document.createElement('div'); tipEl.className = 'ls-fit-tip'; tipEl.setAttribute('role', 'tooltip'); tipEl.id = 'ls-fit-tip'; document.body.appendChild(tipEl); }
    const CAP = 12, rows = hidden.slice(0, CAP).map(tipLabel).filter(Boolean);
    tipEl.replaceChildren(...rows.map(t => { const d = document.createElement('div'); d.textContent = t; return d; }));
    if (hidden.length > CAP) { const d = document.createElement('div'); d.className = 'more'; d.textContent = 'and ' + (hidden.length - CAP) + ' more'; tipEl.appendChild(d); }
    tipEl.hidden = false;
    const r = more.getBoundingClientRect(), w = tipEl.offsetWidth, h = tipEl.offsetHeight;
    const left = Math.max(8, Math.min(window.innerWidth - w - 8, r.right - w));
    const top = r.top - h - 8 >= 8 ? r.top - h - 8 : Math.min(window.innerHeight - h - 8, r.bottom + 8);
    tipEl.style.left = left + 'px'; tipEl.style.top = top + 'px';
    more.setAttribute('aria-describedby', 'ls-fit-tip');
  }
  function hideTip() { if (tipEl) tipEl.hidden = true; }
  /** The same tip for any element carrying its own lines (data-tip, one
   *  line per newline). */
  function showLines(el) {
    const lines = String(el.dataset.tip || '').split('\n').filter(Boolean);
    if (!lines.length) return;
    if (!tipEl) { tipEl = document.createElement('div'); tipEl.className = 'ls-fit-tip'; tipEl.setAttribute('role', 'tooltip'); tipEl.id = 'ls-fit-tip'; document.body.appendChild(tipEl); }
    tipEl.replaceChildren(...lines.slice(0, 12).map(t => { const d = document.createElement('div'); d.textContent = t; return d; }));
    tipEl.hidden = false;
    const r = el.getBoundingClientRect(), w = tipEl.offsetWidth, h = tipEl.offsetHeight;
    tipEl.style.left = Math.max(8, Math.min(window.innerWidth - w - 8, r.right - w)) + 'px';
    tipEl.style.top = (r.top - h - 8 >= 8 ? r.top - h - 8 : Math.min(window.innerHeight - h - 8, r.bottom + 8)) + 'px';
  }
  document.addEventListener('mouseover', (e) => { const t = e.target.closest && e.target.closest('[data-tip]'); if (t) showLines(t); });
  document.addEventListener('mouseout', (e) => { const t = e.target.closest && e.target.closest('[data-tip]'); if (t && !t.contains(e.relatedTarget)) hideTip(); });
  document.addEventListener('focusin', (e) => { const t = e.target.closest && e.target.closest('[data-tip]'); if (t) showLines(t); });
  document.addEventListener('focusout', (e) => { if (e.target.closest && e.target.closest('[data-tip]')) hideTip(); });
  window.addEventListener('scroll', hideTip, { passive: true, capture: true });
  function wireMore(more, list) {
    more.__fitList = list;
    if (more.__fitTip) return;
    more.__fitTip = true;
    more.tabIndex = 0;
    more.classList.add('ls-fit-more-hint');
    more.addEventListener('mouseenter', () => showTip(more));
    more.addEventListener('focus', () => showTip(more));
    more.addEventListener('click', (e) => { e.stopPropagation(); showTip(more); });
    more.addEventListener('mouseleave', hideTip);
    more.addEventListener('blur', hideTip);
    more.addEventListener('keydown', (e) => { if (e.key === 'Escape') hideTip(); });
  }

  function fitRows(list, box, across) {
    const over = across ? overflowsX : overflows;
    const items = [...list.children].filter(n => n.tagName !== 'TEMPLATE' && !n.hasAttribute('data-fit-keep'));
    const more = list.querySelector('[data-fit-more]');
    if (more) wireMore(more, list);
    const setMore = (n) => {
      if (!more) return;
      const t = n ? '+' + n + (more.dataset.fitMore === 'short' ? '' : ' more') : '';
      if (more.textContent !== t) more.textContent = t;
      more.hidden = !n;
    };
    const grow = !across && list.hasAttribute('data-fit-grow') && document.documentElement.dataset.lsSkin === 'broadcast';
    if (grow && list.style.zoom) list.style.zoom = '';
    items.forEach(n => { n.style.display = ''; });
    setMore(0);
    if (!(across ? box.clientWidth : box.clientHeight)) return;   // hidden - nothing to measure
    let hidden = 0;
    // Always leave one row: an empty-looking panel is worse than a tight one.
    while (over(box) && hidden < items.length - 1) {
      items[items.length - 1 - hidden].style.display = 'none';
      hidden++;
      setMore(hidden);
    }
    // Down to one row and still no room for the "+N more" line: drop the line,
    // never slice it ("+3 more" showed 10 of 14px at 1366 - audit run 3, FIT-4b).
    if (more && !more.hidden && over(box)) more.hidden = true;
    // data-fit-grow (Broadcast): every row fits with room to spare - a short
    // list in a tall side panel sat in its top half (Pitcher Pulse 64% empty,
    // Double Watch 58% - Broadcast audit v2, PREM-7 / SPACE-6). It grows, as
    // far as the width allows (growUp), never past 1.4x.
    // A list may ask for more (data-fit-grow="1.8"): two bonus rows in a
    // full-height side panel stayed 40-70% empty at 1.4 (audit v2 run 3,
    // FIT-7 / SPACE-4 / SPACE-5).
    if (grow && !hidden) growUp(list, box, items, Math.min(2, parseFloat(list.getAttribute('data-fit-grow')) || 1.4));
  }

  /** Zoom el up into the room left in box, at most `max`, as long as nothing
   *  re-wraps or runs out sideways (the same checks as fitAll's grow). */
  function growUp(el, box, items, max) {
    const last = [...items].reverse().find(n => n.offsetParent);
    if (!last) return;
    const b = box.getBoundingClientRect();
    const used = last.getBoundingClientRect().bottom - b.top;
    if (used <= 0) return;
    const spare = (b.height - 4) / used;
    if (spare <= 1.08) return;
    const wide0 = wideCount(el), tx = texts(el), h0 = tx.map(x => x.getBoundingClientRect().height);
    for (let z = Math.min(max, spare * 0.97); z > 1.04; z -= 0.06) {
      el.style.zoom = String(z);
      if (!overflows(box) && wideCount(el) <= wide0 && !rewrapped(tx, h0, z)) return;
    }
    el.style.zoom = '';
  }

  /** The smallest font on screen inside el, in CSS px before any zoom. */
  function minFont(el) {
    let m = Infinity;
    for (const n of el.querySelectorAll('*')) {
      if (!n.firstChild || n.firstChild.nodeType !== 3 || !n.textContent.trim() || !n.offsetParent) continue;
      const f = parseFloat(getComputedStyle(n).fontSize);
      if (f && f < m) m = f;
    }
    return m === Infinity ? 0 : m;
  }

  // Growing must only make things bigger, never re-wrap them. On a narrow
  // panel the Director's board grew into its headline's column and the
  // headline went one word a line, run under the board (Xav 2026-10-07).
  // So: the on-screen height of every bit of text (a re-wrap grows it by more
  // than the zoom), and how many elements run out of their box sideways.
  const texts = (el) => [...el.querySelectorAll('*')].filter(x => !x.childElementCount && x.textContent.trim());
  function wideCount(el) {
    let n = 0;
    for (const x of el.querySelectorAll('*')) if (x.clientWidth && x.scrollWidth > x.clientWidth + 1) n++;
    return n;
  }
  function rewrapped(nodes, h0, z) {
    return nodes.some((x, i) => h0[i] > 0 && x.getBoundingClientRect().height > h0[i] * z * 1.08);
  }

  function fitAll(el, box, grow, floorPx, whole) {
    el.style.zoom = '';
    delete el.dataset.fitTight;
    // what overflows: the box, or the element itself when it fills the box and
    // clips its own content - the Podium's show did, so the box never saw the
    // 15px it was over (audit run 3, PREM-1)
    const ov = () => (overflows(box) ? box : (overflows(el) ? el : null));
    if (!box.clientHeight || !el.offsetHeight) return;
    // grow: a short show-all list may also scale UP (to grow at most) so a
    // nine-row Dream Team does not sit in the top half of a tall panel (audit
    // run 1, SPACE-4). Shrinking below still applies.
    if (grow > 1 && !ov()) {
      // scrollHeight never reads below clientHeight, so measure the content's
      // own bottom (both rects are viewport px, so the UI zoom cancels out).
      const b = box.getBoundingClientRect(), used = el.getBoundingClientRect().bottom - b.top;
      const spare = b.height / Math.max(1, used);
      if (spare > 1.04) {
        // ...but only as far as the WIDTH allows: on a narrow, tall panel the
        // Director's board grew 1.8x into its headline's column while the
        // rest of the page scaled down with the window (Xav 2026-10-07). Step
        // down until growing re-wraps nothing and puts nothing out sideways.
        const wide0 = wideCount(el), tx = texts(el), h0 = tx.map(x => x.getBoundingClientRect().height);
        for (let z = Math.min(grow, spare * 0.98); z > 1.04; z -= 0.1) {
          el.style.zoom = String(z);
          if (!ov() && wideCount(el) <= wide0 && !rewrapped(tx, h0, z)) return;
        }
        el.style.zoom = '';
      }
    }
    // A legibility floor (floorPx): zooming the whole of it down put Dream
    // Team / Podium / Director text at 6.7-8.6px on a 13" laptop (audit run 2,
    // FIT-13). Before going under the floor, the panel is told it is tight
    // (data-fit-tight) and its CSS drops what it can live without - a team
    // column, the stat leaderboards - then it zooms again, never below.
    const shrink = () => {
      let z = 1;
      el.style.zoom = '';
      for (let i = 0, o = ov(); i < 6 && o; i++, o = ov()) {
        z = Math.max(0.1, z * (o.clientHeight / o.scrollHeight) * 0.995);
        el.style.zoom = String(z);
      }
      return z;
    };
    let z = shrink();
    if (!floorPx) return;
    const mf = minFont(el);
    // ...and tight too when six shrink passes still left it overflowing: the
    // Podium stopped at 0.916, 15px over, with "Chasing" under the steps
    // (audit run 3, PREM-1)
    if (!mf || (z * mf >= floorPx && !ov())) return;
    el.dataset.fitTight = '1';
    z = shrink();
    const mf2 = minFont(el) || mf;
    if (z * mf2 < floorPx) el.style.zoom = String(floorPx / mf2);
    // whole: nothing may be cut, even to keep the floor - the Dream Team is
    // shown whole or not at all (Xav). The floor gives way only after the
    // tight layout has already dropped everything it can.
    if (whole && ov()) shrink();
  }

  /* FLIP for a list's direct children: when rows are added, removed or
   * re-ordered, each row that stays starts where it was and travels to its
   * new place, instead of jumping (a new score shoved the Score Wire's rows
   * 111px, a Bonus Watch re-sort 72px - Broadcast audit run 2, LIVE-18). */
  const REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  // where each keyed card last stood on screen (data-flip-key), across lists:
  // a Your Players card moving from Playing to Done is a NEW node in another
  // column, and it appeared there with no journey (audit v2 run 3, LIVE-7)
  const shared = new Map();
  function flipper(el) {
    const tops = new WeakMap();
    const kids = () => [...el.children].filter(c => c.tagName !== 'TEMPLATE');
    return {
      // left as well as top: Your Players' columns re-sort sideways, and a
      // card moving to the next column teleported (57 moves, dx -649 - audit
      // v2 run 3, LIVE-7)
      record() {
        for (const c of kids()) {
          tops.set(c, [c.offsetLeft, c.offsetTop]);
          const k = c.dataset && c.dataset.flipKey;
          if (k && c.offsetParent) { const r = c.getBoundingClientRect(); shared.set(k, [r.left, r.top]); }
        }
      },
      play() {
        if (REDUCE && REDUCE.matches) return;
        for (const c of kids()) {
          if (!c.offsetParent || !c.animate) continue;
          let dx, dy;
          if (tops.has(c)) {
            const [l, t] = tops.get(c);
            dx = l - c.offsetLeft; dy = t - c.offsetTop;
          } else {
            const k = c.dataset && c.dataset.flipKey, was = k && shared.get(k);
            if (!was) continue;
            const r = c.getBoundingClientRect();
            dx = was[0] - r.left; dy = was[1] - r.top;
          }
          if (Math.abs(dy) > 1 || Math.abs(dx) > 1) {
            c.animate([{ transform: 'translate(' + dx + 'px, ' + dy + 'px)' }, { transform: 'none' }],
                      { duration: 450, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
          }
        }
      },
    };
  }

  function attach(el, watch, deep, fit, self, flip) {
    sweep();
    if (!el || el.__lsFit) return;
    el.__lsFit = true;
    const box = self ? el : (el.closest('[data-fit-box]') || el.parentElement);
    if (!box) return;
    let raf = 0;
    const fl = flip ? flipper(el) : null;
    const run = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!document.contains(el)) return;
        fit(el, box);
        if (fl) fl.record();
      });
    };
    const ro = new ResizeObserver(run);
    ro.observe(box);
    const more = el.querySelector('[data-fit-more]');
    const mo = new MutationObserver((recs) => {
      if (fl && recs.some(r => r.target === el && r.type === 'childList')) fl.play();
      // ...and a style change inside a row (an x-show line or clock showing up)
      // changes its height too. Our own writes are skipped: a row's display
      // (fitRows) and the element's zoom (fitAll).
      if (recs.some(r => !(more && more.contains(r.target))
          && !(r.type === 'attributes' && (r.target === el || r.target.parentElement === el)))) run();
    });
    mo.observe(watch, { childList: true, subtree: deep, attributes: deep, attributeFilter: deep ? ['style'] : undefined });
    active.add({ el, ro, mo });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    // an image arriving (a club crest) changes a row's height without a resize
    // or a DOM change - refit when one loads
    el.addEventListener('load', (e) => { if (e.target && e.target.tagName === 'IMG') run(); }, true);
    run();
  }

  /* trimCols: hide every column whose visible body cells are all blank /
   * dash / zero. A table built for every slate shows PROJ, Δ and STATUS as
   * columns of "–" on a slate without projections (Broadcast audit run 1,
   * PREM-7). Re-checked at most every 1.5s - the live cells flash on every
   * update and a per-mutation pass would cost frames.
   *
   * Columns are matched by NAME (a body cell's data-label = its header's
   * text), never by index, and hidden with a class, never by writing
   * style.display: the first version reset every cell's inline display,
   * which un-hid cells Alpine had hidden with x-show (the header-less Avg
   * column), and every stat header then sat one column left of its numbers -
   * "RTD 177" for Walker (audit run 2, NUM-21 / PREM-1). */
  const EMPTY = /^(|–|-|—|\+?0(\.0+)?)$/;
  const TRIM = 'ls-trim-col';
  // a header may name its cells' data-label explicitly (the "±Pace" header
  // over data-label="Δ" cells)
  const headName = (th) => (th.dataset.col || th.textContent.replace(/[↑↓]/g, '')).trim().toLowerCase();
  function trimCols(table) {
    if (!table || table.__lsTrim) return;
    table.__lsTrim = true;
    // Rows coming or going (a QB / RB filter, a search, the tab first
    // drawing its rows) re-trim at once - the observer runs before the
    // browser paints: on a timer the dashes of the empty columns flashed for
    // a second or two after every filter click (Xav 2026-10-06) and every
    // time the tab opened (Xav 2026-10-07). Text ticking inside the rows
    // keeps the slow timer.
    let t = 0;
    const seen = new Set();   // columns that have shown a value - never trimmed again
    // Hidden until the first pass over FILLED rows has trimmed: Alpine fills
    // the cells a moment after it adds the rows, so the first paint showed
    // an all-"–" column (In with no lineup, Status between plays) that went
    // on the next pass and every column re-measured - a flicker on every
    // open (Xav 2026-10-10). Until then every change re-checks at once.
    // (skin_broadcast.css keeps a trimmed table hidden until data-trimmed is
    // set: the table paints before this attaches, from an x-init $nextTick)
    let waiting = true;
    const reveal = () => { if (waiting) { waiting = false; table.dataset.trimmed = '1'; } };
    setTimeout(reveal, 1200);   // never left hidden: an empty or slow table shows anyway
    const mo = new MutationObserver((recs) => run(waiting || recs.some(r => r.type === 'childList' && r.target.tagName === 'TBODY')));
    function run(fast) {
      if (fast) { if (t) { clearTimeout(t); t = 0; } check(); return; }
      if (!t) t = setTimeout(() => { t = 0; check(); }, 1500);
    }
    function check() {
      if (!document.contains(table)) { mo.disconnect(); return; }
      const head = table.tHead && table.tHead.rows[0];
      const rows = [...(table.tBodies[0] ? table.tBodies[0].rows : [])].filter(r => r.cells.length > 1);
      if (!head || !rows.length) return;
      // Work out the trimmed set first, then touch only the cells whose state
      // changes: clearing and re-adding every 1.5s wrote ~1,100 class
      // attributes / 5s on the Podium while scoring (audit run 3, LIVE-2).
      const want = new Set(), names = [];
      const ths = [...head.cells].slice(1);             // never the name column
      for (const th of ths) {
        if (getComputedStyle(th).display === 'none' && !th.classList.contains(TRIM)) continue;   // hidden by its own rule
        const name = headName(th);
        if (!name) continue;
        const cells = rows.flatMap(r => [...r.cells].filter(c => (c.dataset.label || '').trim().toLowerCase() === name));
        // A column that has had something in it stays: a filter (C / D / G /
        // W) showing only rows where it happens to be blank dropped Value and
        // Line, and every column re-measured - the table changed shape from
        // one pill to the next (Xav 2026-10-10). Columns only ever come back.
        if (seen.has(name)) continue;
        if (!cells.length || !cells.every(c => EMPTY.test(c.textContent.trim()))) { if (cells.length) seen.add(name); continue; }
        want.add(th);
        cells.forEach(c => want.add(c));
        names.push(name);
      }
      // the trimmed names on the table too, so rows a filter brings in are
      // hidden by CSS from their first paint (skin_broadcast.css data-trim)
      const tv = names.length ? '|' + names.join('|') + '|' : '';
      if ((table.dataset.trim || '') !== tv) table.dataset.trim = tv;
      table.querySelectorAll('.' + TRIM).forEach(c => { if (!want.has(c)) c.classList.remove(TRIM); });
      want.forEach(c => { if (!c.classList.contains(TRIM)) c.classList.add(TRIM); });
      // the rows are filled once the name column says something
      if (waiting && rows[0].cells[0] && rows[0].cells[0].textContent.trim()) reveal();
    }
    mo.observe(table, { childList: true, subtree: true, characterData: true });
    run(true);
  }

  /* freezeCols: a table's columns keep the widths they first drew at. With
   * the browser's auto layout every sort, filter or score tick re-measured
   * the columns from the rows on show - a sort arrow appearing, a longer
   * name rising to the top - and the whole grid shifted sideways on each
   * click (Xav 2026-10-07). Measured once from the natural layout, then
   * locked (table-layout: fixed); measured again only when the panel's
   * width or the set of visible columns changes. */
  function freezeCols(table) {
    if (!table || table.__lsFreeze || !table.parentElement || !window.ResizeObserver) return;
    table.__lsFreeze = true;
    let raf = 0, lastW = 0, lastSig = '';
    const heads = () => (table.tHead && table.tHead.rows[0] ? [...table.tHead.rows[0].cells] : []);
    const sig = () => heads().map(th => (th.style.display === 'none' || th.classList.contains(TRIM) ? 0 : 1)).join('') + '|' + (table.dataset.trim || '');
    function measure() {
      raf = 0;
      if (!document.contains(table)) { ro.disconnect(); mo.disconnect(); return; }
      const w = table.parentElement.clientWidth;
      const body = table.tBodies[0];
      if (!w || !body || !body.rows.length) return;          // hidden tab / no rows yet
      const sg = sig();
      if (w === lastW && sg === lastSig) return;
      lastW = w; lastSig = sg;
      const ths = heads();
      table.style.tableLayout = '';
      ths.forEach(th => { th.style.width = ''; });
      // a [data-fill] column (All Players' one-line stat Line) is measured
      // wrapped and then takes whatever the others leave - measured on one
      // line it pushed the table past its scroller (V6)
      table.classList.add('ls-measuring');
      const ws = ths.map(th => th.getBoundingClientRect().width);
      table.classList.remove('ls-measuring');
      ths.forEach((th, i) => { if (ws[i] && !th.hasAttribute('data-fill')) th.style.width = ws[i] + 'px'; });
      table.style.tableLayout = 'fixed';
    }
    const schedule = () => { if (!raf) raf = requestAnimationFrame(measure); };
    const ro = new ResizeObserver(schedule);
    ro.observe(table.parentElement);
    // a column shown / hidden, or the first rows arriving - not text ticking
    const mo = new MutationObserver((recs) => {
      if (recs.some(r => r.target.tagName === 'TH' || r.attributeName === 'data-trim'
          || (r.type === 'childList' && r.target.tagName === 'TBODY' && !lastW))) schedule();
    });
    mo.observe(table, { attributes: true, attributeFilter: ['style', 'class', 'data-trim'], subtree: true, childList: true });
    schedule();
  }

  /* fade: a scroller of cards (the scoreboard rail) keeps scrolling - every
   * game stays reachable - but the card cut by its bottom edge sits wholly
   * inside the fade, so no card reads as sliced in half (audit run 3, FIT-22 /
   * PREM-6). Sets --fit-fade on the scroller; its CSS mask reads it. */
  function fade(scroller, sel) {
    if (!scroller || scroller.__lsFade) return;
    scroller.__lsFade = true;
    let raf = 0;
    const run = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!document.contains(scroller)) return;
        const b = scroller.getBoundingClientRect().bottom, edge = b;
        // Nothing below to scroll to (it all fits, or it is scrolled to the
        // end): no fade at all - the last card's foot read washed out under
        // a fade with nothing behind it (Xav 2026-10-06).
        const more = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 2;
        let f = more ? 28 : 0;
        if (more) for (const c of scroller.querySelectorAll(sel)) {
          const r = c.getBoundingClientRect();
          if (!r.height) continue;
          // only a card mostly below the edge goes into the fade: a big live
          // card that is nearly whole keeps the short fade, not a 250px veil
          if (r.top < edge - 1 && r.bottom > edge + 1) {
            if (edge - r.top <= r.height * 0.5 || r.height < 60) f = Math.max(28, b - r.top + 4);   // a short row: always wholly in the fade
            break;
          }
        }
        const v = Math.round(f) + 'px';
        if (scroller.style.getPropertyValue('--fit-fade') !== v) scroller.style.setProperty('--fit-fade', v);
        // and sideways: a fade on the right only while there is more to the
        // right - a table that fits stopped 28px short of the panel for a
        // fade over nothing (Broadcast audit v2, SPACE-5)
        const vx = (scroller.scrollWidth - scroller.scrollLeft - scroller.clientWidth > 2) ? '28px' : '0px';
        if (scroller.style.getPropertyValue('--fit-fade-x') !== vx) scroller.style.setProperty('--fit-fade-x', vx);
      });
    };
    scroller.addEventListener('scroll', run, { passive: true, capture: true });   // capture: an inner box may be the one scrolling
    const ro = new ResizeObserver(run); ro.observe(scroller);
    const mo = new MutationObserver(run); mo.observe(scroller, { childList: true, subtree: true });
    active.add({ el: scroller, ro, mo });
    run();
  }

  /* rail: the scoreboard rail (Xav 2026-10-06). No fade. The cards fit with
   * no scroll when they can do it legibly: first they go tight (each card
   * drops its period-by-period line, .nfl-line / .nhl-line), then they zoom
   * down - never below RAIL_ZOOM_MIN (MLB's four cards sat at 0.63, v2 run 1
   * SPACE-2). Past that the cards stay whole at full size and the rail
   * scrolls, paged by an up / down pair in a strip of its own under the
   * cards: the arrows sat on top of the last card's text (PREM-6 / FIT-13 /
   * SPACE-1). */
  const RAIL_ZOOM_MIN = 0.8;
  function rail(scroller) {
    if (!scroller || scroller.__lsRail) return;
    scroller.__lsRail = true;
    const host = scroller.parentElement;
    const pager = document.createElement('div');
    pager.className = 'ls-rail-pager';
    pager.hidden = true;
    host.appendChild(pager);
    const mk = (dir) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ls-rail-arrow is-' + dir;
      b.setAttribute('aria-label', dir === 'up' ? 'Scroll the scoreboard up' : 'Scroll the scoreboard down');
      b.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="' + (dir === 'up' ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6') + '"/></svg>';
      b.addEventListener('click', () => scroller.scrollBy({ top: (dir === 'up' ? -1 : 1) * scroller.clientHeight * 0.8, behavior: 'smooth' }));
      pager.appendChild(b);
      return b;
    };
    const up = mk('up'), down = mk('down');
    let raf = 0;
    // The pager shows while the rail scrolls at all (not per position, so the
    // cards never jump as you reach an end); an end's arrow greys out.
    const arrows = () => {
      const scrolls = scroller.scrollHeight - scroller.clientHeight > 2;
      if (pager.hidden !== !scrolls) pager.hidden = !scrolls;
      const top = scroller.scrollTop > 2;
      const more = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 2;
      if (up.disabled !== !top) up.disabled = !top;
      if (down.disabled !== !more) down.disabled = !more;
    };
    const over = () => scroller.scrollHeight - scroller.clientHeight > 2;
    const fitRail = (board) => {
      board.style.zoom = '';
      delete board.dataset.fitTight;
      pager.hidden = true;   // measure the rail at its full height
      if (!over()) return;
      board.dataset.fitTight = '1';
      if (!over()) return;
      let z = 1;
      for (let i = 0; i < 6 && over(); i++) {
        z = z * (scroller.clientHeight / scroller.scrollHeight) * 0.995;
        if (z < RAIL_ZOOM_MIN) break;
        board.style.zoom = String(z);
      }
      if (z >= RAIL_ZOOM_MIN && !over()) return;
      // too many to fit legibly: whole cards, full size, and scroll
      board.style.zoom = '';
      delete board.dataset.fitTight;
    };
    const run = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!document.contains(scroller)) return;
        // the scoreboard showing (the rail holds one per sport, the rest hidden)
        const board = [...scroller.querySelectorAll('.ls-sb')].find(b => b.offsetParent);
        // Broadcast only: Simple / Analytical keep their rail as it was
        if (document.documentElement.dataset.lsSkin !== 'broadcast') {
          if (board) { board.style.zoom = ''; delete board.dataset.fitTight; }
          pager.hidden = true;
          return;
        }
        if (board && board.querySelector('.ls-sb-card')) fitRail(board);
        else if (board) { board.style.zoom = ''; delete board.dataset.fitTight; }
        // each card's "mine" line: two players whole, or one and "+N" (FIT-3)
        if (board) board.querySelectorAll('.ls-sb-mine').forEach((m) => {
          const names = m.querySelector('.ls-sb-mine-names');
          m.classList.remove('is-one');
          if (names && names.querySelectorAll('.ls-sb-mine-p').length > 1 && names.scrollWidth > names.clientWidth + 1) m.classList.add('is-one');
        });
        arrows();
      });
    };
    scroller.addEventListener('scroll', arrows, { passive: true });
    const ro = new ResizeObserver(run); ro.observe(scroller);
    // content only - the zoom this writes must not re-trigger it
    const mo = new MutationObserver(run); mo.observe(scroller, { childList: true, subtree: true, characterData: true });
    active.add({ el: scroller, ro, mo });
    run();
  }

  /* lineFit: a stat line ("428 pass yds · 1 TD · 6 rush yds · 1 rush TD ·
   * 2 INT") on ONE line in its cell - stats that do not fit go whole from
   * the end and a "+N" says how many; the full line is the cell's tooltip.
   * Wrapped, the All Players rows ran 31-95px tall (audit v2 run 3, SPACE-12
   * / PREM-7); ellipsized, a stat read "1 rus…" (V6: nothing cut). The
   * cells are Alpine x-text: every rewrite re-fits in the same mutation pass,
   * before the browser paints. */
  function lineFit(table, sel) {
    if (!table || table.__lsLineFit) return;
    table.__lsLineFit = true;
    const SEP = ' · ';
    let busy = false;
    const fitCell = (td) => {
      const full = td.textContent;
      if (td.__lsFitted === full) return;               // our own write
      td.removeAttribute('title');
      if (td.scrollWidth <= td.clientWidth + 1 || full.indexOf(SEP) === -1) { td.__lsFitted = full; return; }
      const parts = full.split(SEP);
      // longest first: "+2 more" ("+2" alone read as points), then "+2",
      // then the first stat on its own - the whole line is the tooltip
      const tries = [];
      for (let n = parts.length - 1; n >= 1; n--) tries.push(parts.slice(0, n).join(SEP) + SEP + '+' + (parts.length - n) + ' more');
      for (let n = parts.length - 1; n >= 1; n--) tries.push(parts.slice(0, n).join(SEP) + ' +' + (parts.length - n));
      tries.push(parts[0]);
      for (const txt of tries) {
        td.textContent = txt;
        if (td.scrollWidth <= td.clientWidth + 1) break;
      }
      td.title = full;
      td.__lsFitted = td.textContent;
    };
    const run = () => {
      if (busy || !document.contains(table)) return;
      busy = true;
      try { table.querySelectorAll(sel).forEach(td => { if (td.offsetParent) fitCell(td); }); }
      finally { busy = false; }
    };
    const mo = new MutationObserver(() => { if (!busy) run(); });
    mo.observe(table, { childList: true, subtree: true, characterData: true });
    if (window.ResizeObserver) {
      // a wider cell may hold more: re-fit from the full text
      const ro = new ResizeObserver(() => {
        busy = true;
        try { table.querySelectorAll(sel).forEach(td => { if (td.title) td.textContent = td.title; td.__lsFitted = null; }); }
        finally { busy = false; }
        run();
      });
      ro.observe(table);
      // the column itself narrows when freezeCols locks the widths
      const th = table.tHead && [...table.tHead.rows[0].cells].find(c => (c.dataset.col || '') === 'Line');
      if (th) ro.observe(th);
    }
    run();
  }

  return {
    lineFit,
    trimCols,
    freezeCols,
    fade,
    rail,
    // self: the list is its own clipping box (a flex:1 scroller), not its parent.
    rows(listEl, self) { attach(listEl, listEl, true, fitRows, !!self, true); },
    /* slide: a scrolling list whose items re-sort - each card travels to its
     * new place (FLIP) instead of teleporting (Your Players; Broadcast audit
     * v2 run 2, LIVE-2 / PREM-5). Reduced motion: it just lands. */
    // order: also when a child's inline style changes - The Race re-ranks its
    // lanes with CSS `order`, which moves them without touching the DOM
    // (audit v2 run 3, LIVE-8)
    slide(listEl, order) {
      if (!listEl || listEl.__lsSlide) return;
      listEl.__lsSlide = true;
      const fl = flipper(listEl);
      fl.record();
      const mo = new MutationObserver(() => { fl.play(); fl.record(); });
      mo.observe(listEl, order ? { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] } : { childList: true });
      active.add({ el: listEl, mo, flag: '__lsSlide' });
    },
    chips(rowEl) { attach(rowEl, rowEl, true, (el, box) => fitRows(el, box, true), true); },
    // floorPx: never zoom text below this many px (the panel turns tight first).
    all(el, deep, grow, floorPx, whole) { attach(el, deep ? el : (el.querySelector('tbody') || el), !!deep, (e, b) => fitAll(e, b, grow || 1, floorPx || 0, !!whole)); },
  };
})();
